import { onScopeDispose } from '@vue/composition-api'

import { useAuth } from '@/composables/useAuth'
import { useCentrifugo } from '@/composables/useCentrifugo'
import { DISPATCHER_LISTS_VIEW_PERMISSION } from '@/constants/permissions'
import { DISPATCHER_NOTIFICATION_TYPES } from '@/modules/dispatcher/constants/notifications'
import { wasLocalRouteAction } from '@/modules/dispatcher/utils/localRouteActions'
import { normalizeRoute } from '@/modules/dispatcher/utils/normalizeRoute'
import router from '@/router'
import store from '@/store'
import { createBoundedTtlSet } from '@/utils/boundedTtlSet'
import { notificationService } from '@/utils/notifications'

const LIVE_ADDED_HIGHLIGHT_MS = 8000
const CDC_DEDUP_TTL_MS = 10 * 60 * 1000
const processedCdcEvents = createBoundedTtlSet({
    ttlMs: CDC_DEDUP_TTL_MS,
    maxSize: 5000,
})

export function useDispatcherNotifications() {
    const { user, hasPermission } = useAuth()
    const { connect, subscribe, unsubscribe } = useCentrifugo()

    const isValidNotification = data => {
        if (!data || typeof data !== 'object') return false

        if (
            !data.cmd ||
            !Object.values(DISPATCHER_NOTIFICATION_TYPES).includes(data.cmd)
        ) {
            return false
        }
        return true
    }

    /**
     * Обработчик обновления заказа
     */
    const handleOrderUpdated = data => {
        const order = data.data?.order
        if (!order?.id) {
            console.warn(
                '[WS: Dispatcher] Получены некорректные данные order_updated (отсутствует id заказа)'
            )

            if (import.meta.env.DEV) {
                console.log(
                    '[WS: Dispatcher] Сбойный payload order_updated:',
                    data
                )
            }
            return
        }

        // Дедупликация CDC-события по commit_lsn (глобально монотонный id):
        // повторная доставка не должна дважды обновлять список/карточку и тост
        const lsn = data.meta?.commit_lsn
        if (lsn !== undefined && lsn !== null) {
            const dedupKey = `${lsn}:${order.id}`
            if (processedCdcEvents.has(dedupKey)) return
            processedCdcEvents.add(dedupKey)
        }

        const currentRouteName = router.currentRoute.name
        const currentOrderId = router.currentRoute.params.id

        // На странице списка заказов — частичное обновление в таблице и маршруте
        if (currentRouteName === 'dispatcher') {
            store.commit('dispatcher/ADD_OR_UPDATE_ORDER', {
                order,
                prepend: true,
            })
            setTimeout(() => {
                store.commit('dispatcher/CLEAR_ORDER_LIVE_ADDED', order.id)
            }, LIVE_ADDED_HIGHLIGHT_MS)
            store.commit('dispatcher/UPDATE_ORDER_IN_ROUTE', {
                order,
            })
            // Сигнал странице диспетчера перезапросить счётчики статусов:
            // CDC обновляет сам заказ, но агрегаты (statusesCount) считаются
            // на бэке и без бампа версии становятся устаревшими до смены
            // фильтра/обновления страницы (EDS-5102).
            store.commit('dispatcher/BUMP_CDC_ORDER_VERSION')
        }

        // На странице карточки заказа — полное обновление (только если это тот же заказ)
        if (
            currentRouteName === 'dispatcher-order-details' &&
            currentOrderId === String(order.id)
        ) {
            store.commit('dispatcher/UPDATE_CURRENT_ORDER', order)
        }

        const notificationMessage = data.data?.message
        if (
            !notificationMessage ||
            Object.keys(notificationMessage).length === 0
        ) {
            console.warn(
                `[WS: Dispatcher] Отсутствует тело сообщения (message) в событии заказа ${order.num}`
            )

            if (import.meta.env.DEV) {
                console.log('[WS: Dispatcher] Данные без сообщения:', data)
            }
            return
        }

        // Показываем уведомление
        store.commit(
            'orders/SET_DISPATCHER_ORDER_NOTIFICATION',
            {
                orderId: order.id,
                orderNumber: order.num,
                notificationType: notificationMessage?.type,
                notificationText: notificationMessage?.text,
            },
            { root: true }
        )
    }

    /**
     * Маршрут относится к текущей выборке по дивизиону и диапазону дат, т.е.
     * влияет на агрегаты routeStatusesCount (счётчики статусов считаются по
     * всем статусам в пределах выборки, поэтому статус-фильтр здесь не
     * учитывается — иначе CDC переход в другой статус не пересчитает плашки).
     */
    const affectsCurrentRouteCounts = route => {
        const filters = store.state.dispatcher.routeFilters
        if (!filters) return false

        if (
            filters.division_id &&
            route.division_id &&
            route.division_id !== filters.division_id
        ) {
            return false
        }

        if (filters.start_date && filters.end_date) {
            const routeDay = String(route.date).slice(0, 10) // YYYY-MM-DD
            if (routeDay < filters.start_date || routeDay > filters.end_date) {
                return false
            }
        }

        return true
    }

    /**
     * Проверяет, относится ли маршрут к текущей выборке панели маршрутов
     * (дивизион, диапазон дат, фильтр статуса). Нужно, чтобы CDC-INSERT не
     * подмешивал в список маршрут из другой даты/дивизиона. Фильтры по курьеру
     * и номеру заказа применяются на клиенте в filteredRoutes, поэтому здесь
     * не дублируются.
     */
    const isRouteInCurrentView = route => {
        if (!affectsCurrentRouteCounts(route)) return false

        const filters = store.state.dispatcher.routeFilters
        if (filters.status && route.status?.id !== filters.status) {
            return false
        }

        return true
    }

    /**
     * Обработчик обновления маршрута (CDC route_updated: INSERT/UPDATE).
     * Существующий маршрут — патчим, новый — добавляем, если он попадает
     * в текущую выборку панели.
     */
    const handleRouteUpdated = data => {
        const rawRoute = data.data
        if (!rawRoute?.id) {
            console.warn(
                '[WS: Dispatcher] Получены некорректные данные route_updated (отсутствует id маршрута)'
            )

            if (import.meta.env.DEV) {
                console.log(
                    '[WS: Dispatcher] Сбойный payload route_updated:',
                    data
                )
            }
            return
        }

        // EDS-5111: уведомление о смене курьера / завершении маршрута
        // (Отменён/Исполнен). Бэк присылает message только для значимых
        // изменений; чистый data-sync (route_order, capacity) приходит без него.
        // Если действие выполнил сам этот диспетчер — локальный тост уже показан,
        // CDC-эхо гасим (дубль). Другие клиенты/liveupdate метки не имеют.
        const { message } = data
        if (message?.text && !wasLocalRouteAction(rawRoute.id)) {
            notificationService.showMessage(
                message.text,
                message.title || null,
                {
                    variant: message.type || 'info',
                }
            )
        }

        // Панель маршрутов есть только на странице списка диспетчера.
        if (router.currentRoute.name !== 'dispatcher') return

        // CDC присылает status строкой, стор/RouteCard ждут объект { id, name }.
        const route = normalizeRoute(rawRoute)

        const existing = store.getters['dispatcher/getRouteById'](route.id)
        if (existing) {
            const nextRouteState = {
                ...existing,
                ...route,
                orders: route.orders ?? existing.orders,
            }

            if (!isRouteInCurrentView(nextRouteState)) {
                store.commit('dispatcher/REMOVE_ROUTE', route.id)
            } else {
                store.commit('dispatcher/UPDATE_ROUTE', route)
            }

            store.commit('dispatcher/BUMP_CDC_ROUTE_VERSION')
            return
        }

        // Маршрут вне дивизиона/диапазона дат не влияет ни на список,
        // ни на счётчики статусов текущей выборки.
        if (!affectsCurrentRouteCounts(route)) return

        // В список добавляем только если попадает и под статус-фильтр; на
        // счётчики статусов событие повлияет в любом случае (новый маршрут
        // в каком-то статусе в пределах выборки).
        if (isRouteInCurrentView(route)) {
            store.commit('dispatcher/ADD_ROUTE', route)
        }
        store.commit('dispatcher/BUMP_CDC_ROUTE_VERSION')
    }

    /**
     * Обработчик удаления маршрута (CDC route_deleted, EDS-5107). Снимает маршрут
     * со списка и бампит cdcRouteVersion, чтобы плашки статус-фильтра пересчитались
     * (счётчики routeStatusesCount считает бэк по запросу).
     */
    const handleRouteDeleted = data => {
        const payload = data.data
        if (!payload?.id) {
            console.warn(
                '[WS: Dispatcher] Получены некорректные данные route_deleted (отсутствует id маршрута)'
            )

            if (import.meta.env.DEV) {
                console.log(
                    '[WS: Dispatcher] Сбойный payload route_deleted:',
                    data
                )
            }
            return
        }

        if (router.currentRoute.name !== 'dispatcher') return

        const filters = store.state.dispatcher.routeFilters
        if (
            filters?.division_id &&
            payload.division_id &&
            payload.division_id !== filters.division_id
        ) {
            return
        }

        store.commit('dispatcher/REMOVE_ROUTE', payload.id)
        store.commit('dispatcher/BUMP_CDC_ROUTE_VERSION')
    }

    /**
     * Обработчик входящих уведомлений по диспетчеру
     * @param {object} data - Данные, пришедшие от Centrifugo
     */
    const handlePublication = data => {
        if (!isValidNotification(data)) {
            console.warn(
                '[WS: Dispatcher] Пропущено некорректное уведомление из-за невалидной структуры.'
            )

            if (import.meta.env.DEV) {
                console.log('[WS: Dispatcher] Невалидные данные:', data)
            }
            return
        }

        // Обработка по типу команды
        switch (data.cmd) {
            case DISPATCHER_NOTIFICATION_TYPES.ORDER_UPDATED:
                handleOrderUpdated(data)
                break

            case DISPATCHER_NOTIFICATION_TYPES.ROUTE_UPDATED:
                handleRouteUpdated(data)
                break

            case DISPATCHER_NOTIFICATION_TYPES.ROUTE_DELETED:
                handleRouteDeleted(data)
                break

            default:
                if (import.meta.env.DEV) {
                    console.debug(
                        '[WS: Dispatcher] Необработанный тип события:',
                        data.cmd
                    )
                }
                break
        }
    }

    /**
     * Инициализирует подключение и подписки на канал диспетчера
     */
    const init = (prefix, routePrefix) => {
        if (!user) return

        const { divisions } = user.value
        const hasDivisions = divisions && divisions.length > 0

        if (!hasDivisions || !hasPermission(DISPATCHER_LISTS_VIEW_PERMISSION)) {
            return
        }

        connect()

        const firstDivisionId = divisions[0]?.id
        if (firstDivisionId) {
            const channel = `${prefix}:${firstDivisionId}`
            subscribe(channel, handlePublication)

            let routeChannel = null
            if (routePrefix) {
                routeChannel = `${routePrefix}:${firstDivisionId}`
                subscribe(routeChannel, handlePublication)
            }

            onScopeDispose(() => {
                console.log(
                    `[WS: Dispatcher] Отписка от ${channel}:${firstDivisionId}`
                )
                unsubscribe(channel)
                if (routeChannel) {
                    unsubscribe(routeChannel)
                }
            })
        }
    }

    return {
        initDispatcherNotifications: init,
    }
}
